import { useEffect, useState } from "react";
import {
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    FormHelperText,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from "@mui/material";
import { makeStyles } from "@/compat/muiStyles";
import { grey } from "@mui/material/colors";
import { Add, Close, WarningAmber } from "@mui/icons-material";
import { nanoid } from "nanoid";
import clsx from "clsx";
import { useSnackbar } from "notistack";
import { useHistory, useRouteMatch } from "@/compat/router";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import PageBase from "../../../components/PageBase";
import CardBase from "../../../components/cards/CardBase";
import CustomButton from "../../../components/CustomButton";
import SiteSelect from "../../../components/SiteSelect";
import HazardPickerDialog from "../../components/riskassessments/HazardPickerDialog";
import PeopleAtRiskCard from "../../components/riskassessments/PeopleAtRiskCard";
import TemplateVersionHistoryDialog from "../../components/riskassessments/dialogs/TemplateVersionHistoryDialog";
import moduleAreas from "../../constants/moduleAreaConstants";
import useSectors from "../../hooks/queries/useSectors";
import useHazards from "../../hooks/queries/useHazards";
import usePeople from "../../hooks/queries/usePeople";
import useAddTemplate from "../../hooks/mutations/useAddTemplate";
import useUpdateTemplate from "../../hooks/mutations/useUpdateTemplate";
import useTemplateVersion from "../../hooks/queries/useTemplateVersion";
import useAllEnabledModuleTemplates from "../../hooks/queries/useAllEnabledModuleTemplates";
import useAccountAppSites from "../../../hooks/queries/useAccountAppSites";
import { useAuth } from "../../../contexts/authContext";
import TemplateBuilderHelper from "../../utils/TemplateBuilderHelper";
import {
    clearTemplate,
    setIncidentType,
    setIsGlobalTemplate,
    setTemplateApplicationArea,
    setTemplateFrequency,
    setTemplateFrequencyInterval,
    setTemplateName,
    setTemplateSectorId,
} from "../../../redux/actions/templateBuilderActions";
import { selectSite } from "../../../redux/actions/accountActions";

const useStyles = makeStyles((theme) => ({
    header: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexWrap: "wrap",
        gap: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    title: {
        fontFamily: ["Sailec", "Arial", "sans-serif"].join(","),
        fontWeight: 700,
    },
    subtitle: {
        color: theme.palette.text.secondary,
    },
    summaryChips: {
        display: "flex",
        gap: theme.spacing(1),
        flexWrap: "wrap",
        marginBottom: theme.spacing(2),
    },
    guidance: {
        backgroundColor: theme.palette.mode === "dark" ? grey[900] : "#f4f1ff",
        border: `1px solid ${theme.palette.mode === "dark" ? grey[800] : "#e5defb"}`,
        borderRadius: theme.spacing(1),
        padding: theme.spacing(2),
    },
    section: {
        marginBottom: theme.spacing(2),
    },
    tile: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1.5),
        width: "100%",
        height: "100%",
        textAlign: "left",
        font: "inherit",
        color: "inherit",
        padding: theme.spacing(1.25, 1.5),
        borderRadius: theme.spacing(1),
        border: `1.5px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        cursor: "pointer",
        transition: "border-color .12s, background-color .12s, box-shadow .12s",
        "&:hover": {
            borderColor: theme.palette.secondary.light,
            boxShadow: theme.shadows[1],
        },
        "&:focus-visible": {
            outline: `2px solid ${theme.palette.secondary.main}`,
            outlineOffset: 2,
        },
    },
    tileSelected: {
        borderColor: theme.palette.secondary.main,
        backgroundColor: "rgba(88, 72, 201, 0.06)",
    },
    tileStatic: {
        cursor: "default",
        "&:hover": {
            borderColor: theme.palette.secondary.main,
            boxShadow: "none",
        },
    },
    tileIcon: {
        height: 32,
        width: 32,
        objectFit: "contain",
        flexShrink: 0,
    },
    tileLabel: {
        flex: 1,
        fontSize: "0.85rem",
        lineHeight: 1.3,
    },
    selectedCount: {
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(1.5),
    },
    empty: {
        color: theme.palette.text.secondary,
        padding: theme.spacing(2),
        textAlign: "center",
    },
    customList: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(1),
        marginBottom: theme.spacing(2),
    },
    customHazardRow: {
        border: `1.5px solid ${theme.palette.secondary.main}`,
        backgroundColor: "rgba(88, 72, 201, 0.04)",
        borderRadius: theme.spacing(1),
        padding: theme.spacing(2),
    },
    customHazardHead: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
    },
    customHazardName: {
        fontWeight: 600,
        fontSize: "0.95rem",
    },
    headSpacer: {
        flexGrow: 1,
    },
    customControls: {
        marginTop: theme.spacing(1.5),
        paddingTop: theme.spacing(1.5),
        borderTop: `1px dashed ${theme.palette.divider}`,
    },
    controlsLabel: {
        textTransform: "uppercase",
        fontSize: "0.72rem",
        letterSpacing: 0.5,
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(1),
    },
    noControls: {
        fontSize: "0.85rem",
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(1),
    },
    customControlRow: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
        padding: theme.spacing(0.25, 0),
    },
    customControlName: {
        flex: 1,
        fontSize: "0.9rem",
    },
    addControlRow: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
        marginTop: theme.spacing(1.5),
        maxWidth: 640,
    },
    footer: {
        position: "sticky",
        bottom: 0,
        zIndex: 2,
        marginTop: theme.spacing(2),
        padding: theme.spacing(1.5, 2),
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: theme.spacing(2),
        backgroundColor: theme.palette.background.paper,
        borderTop: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(1),
        boxShadow: theme.shadows[3],
    },
    footerButtons: {
        display: "flex",
        gap: theme.spacing(2),
    },
}));

const hazardImagesUrl = `${process.env.PUBLIC_URL}/images/hazardIcons/`;
const fallbackHazardIcon = `${hazardImagesUrl}General warning.svg`;

/**
 * Single-view Risk Assessment template builder — replaces the 3-step stepper
 * (Set Up / Hazards / People) with one scrolling page. Presentation only:
 * it drives the same redux templateBuilder slice + TemplateBuilderHelper payload
 * and the same add/update hooks as the legacy stepper, so the saved template is
 * byte-identical to the previous flow.
 */
export default function RiskAssessmentTemplateBuilder() {
    const classes = useStyles();
    const history = useHistory();
    const dispatch = useAppDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const { isWorknestUser } = useAuth();

    const match = useRouteMatch();
    const { templateVersionId, isCopy } = match.params;
    const moduleArea = moduleAreas.RISKASSESSMENTS;

    const { selectedSite, selectedAccount, sites, activeAppId, appSiteIds } = useAppSelector(
        (state) => state.account
    );
    const { templateName, templateSectorId, isGlobalTemplate, templateFrequency, incidentType, templateFrequencyInterval } =
        useAppSelector((state) => state.templateBuilder);

    const [templateDescription, setTemplateDescription] = useState("");
    const [templateHazardIds, setTemplateHazardIds] = useState<any[]>([]);
    const [templatePeopleIds, setTemplatePeopleIds] = useState<any[]>([]);
    const [newCustomHazards, setNewCustomHazards] = useState<any[]>([]);
    const [newCustomPeople, setNewCustomPeople] = useState<any[]>([]);
    const [hazardPickerOpen, setHazardPickerOpen] = useState(false);
    const [controlDrafts, setControlDrafts] = useState<Record<string, string>>({});
    const [versionDialogOpen, setVersionDialogOpen] = useState(false);
    const [selectedTemplateId, setSelectedTemplateId] = useState<any>();
    const [changeNotes, setChangeNotes] = useState("");
    const [showChangeNotesDialog, setShowChangeNotesDialog] = useState(false);

    const sectors = useSectors();
    const hazards = useHazards();
    const people = usePeople();
    const addNewTemplate = useAddTemplate();
    const updateTemplate = useUpdateTemplate();

    const siteExternalIds = selectedSite ? [selectedSite.externalId] : appSiteIds[activeAppId];

    const { data: accountSitesData } = useAccountAppSites({
        childExternalIds: [
            ...(selectedAccount?.childExternalIds || []),
            selectedAccount?.account?.externalId ?? "",
        ],
        activeAppId,
    });
    const backupSites = accountSitesData?.map((item) => item.externalId);

    // Enabled RA templates — used only to enforce unique template names.
    const { data: raTemplates } = useAllEnabledModuleTemplates({
        moduleArea: moduleAreas.RISKASSESSMENTS,
        accountId: selectedAccount?.account?.externalId ?? "",
        siteExternalIds: siteExternalIds ?? backupSites,
        includeDisabled: false,
    });

    const templateVersion = useTemplateVersion(templateVersionId, {
        onSuccess: onTemplateVersionSuccess,
    });

    function onTemplateVersionSuccess(data) {
        if (data?.id && !templateName) {
            dispatch(setIsGlobalTemplate({ isGlobalTemplate: !data.siteExternalId }));
            dispatch(setTemplateName({ text: isCopy ? `${data.name} - Copy` : data.name, hasUpdated: false }));
            dispatch(setTemplateSectorId({ sectorId: data.sectorId, hasUpdated: false }));
            // Seed the frequency/interval/incidentType fields the RA flow doesn't surface, so the
            // update payload matches the legacy stepper (which coerces a null interval to 0).
            dispatch(setTemplateFrequency({ freq: data.frequency, hasUpdated: false }));
            dispatch(setTemplateFrequencyInterval({ frequencyInterval: data.frequencyInterval == null ? 0 : data.frequencyInterval, hasUpdated: false }));
            dispatch(setIncidentType({ incidentType: data.incidentType, hasUpdated: false }));
            setTemplateDescription(data.description ?? "");
            setTemplateHazardIds(data.templateHazards?.map((x) => x.hazardId) ?? []);
            setTemplatePeopleIds(data.templatePeoples?.map((x) => x.peopleId) ?? []);
            setSelectedTemplateId(data.templateId);
            dispatch(selectSite(sites[data.siteExternalId] ?? null));
            // Controls come back as plain strings; the editor keys them by id.
            setNewCustomHazards(
                (data.customHazards ?? []).map((hazard) => ({
                    ...hazard,
                    controls: (hazard.controls ?? []).map((name) => ({ id: nanoid(5), name })),
                }))
            );
            setNewCustomPeople(
                (data.customPeople ?? []).map((person) => ({ id: person.id ?? nanoid(5), name: person.name, siteExternalId: person.siteExternalId }))
            );
        }
    }

    useEffect(() => {
        dispatch(setTemplateApplicationArea(moduleArea));
        return () => {
            dispatch(clearTemplate());
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    const totalHazards = templateHazardIds.length + newCustomHazards.length;
    const detailsComplete = !!templateName?.trim() && !!templateSectorId && !!templateDescription?.trim();
    const selectedHazards = (hazards?.data ?? []).filter((h) => templateHazardIds.includes(h.id));

    function removeHazard(id) {
        setTemplateHazardIds((ids) => ids.filter((x) => x !== id));
    }

    function removeCustomHazard(id) {
        setNewCustomHazards((customs) => customs.filter((c) => c.id !== id));
    }

    function addCustomHazardControl(hazardId) {
        const name = (controlDrafts[hazardId] ?? "").trim();
        if (!name) return;
        setNewCustomHazards((customs) =>
            customs.map((c) =>
                c.id === hazardId
                    ? {
                          ...c,
                          controls: (c.controls ?? []).some(
                              (ctrl) => ctrl.name.trim().toLowerCase() === name.toLowerCase()
                          )
                              ? c.controls
                              : [...(c.controls ?? []), { id: nanoid(5), name }],
                      }
                    : c
            )
        );
        setControlDrafts((d) => ({ ...d, [hazardId]: "" }));
    }

    function removeCustomHazardControl(hazardId, controlId) {
        setNewCustomHazards((customs) =>
            customs.map((c) =>
                c.id === hazardId
                    ? { ...c, controls: (c.controls ?? []).filter((ctrl) => ctrl.id !== controlId) }
                    : c
            )
        );
    }

    const totalPeople = templatePeopleIds.length + newCustomPeople.length;

    function goBack() {
        dispatch(clearTemplate());
        history.goBack();
    }

    function isNameUnique() {
        if (!raTemplates) return true;
        const name = templateName.trim();
        if (isCopy || !templateVersion.data) {
            return !raTemplates.some((t) => t.name === name);
        }
        return !raTemplates.some((t) => t.name === name && t.templateVersionId !== templateVersion.data.id);
    }

    function validate() {
        if (!templateName?.trim()) return "Enter a template name.";
        if (!isNameUnique()) return "You must select a unique name.";
        if (!templateSectorId) return "Select a sector.";
        if (!templateDescription?.trim()) return "Enter a description.";
        // A custom-only template (no library hazards) saves fine on the backend, so count both.
        if (totalHazards === 0) return "You must add at least one hazard.";
        if (templatePeopleIds.length === 0) return "You must select at least one type of person.";
        return null;
    }

    function save() {
        const newTemplate = new TemplateBuilderHelper(
            templateName,
            moduleArea,
            templateDescription,
            templateFrequency,
            incidentType,
            templateFrequencyInterval,
            undefined
        )
            .withSectorId(templateSectorId)
            .withHazardIds(templateHazardIds)
            .withPeopleIds(templatePeopleIds)
            .withRiskAssessmentSections(templateHazardIds, hazards?.data, newCustomHazards)
            .withCustomHazards(newCustomHazards)
            .withCustomPeople(newCustomPeople);

        if (templateVersionId) {
            newTemplate.withTemplateVersionId(templateVersionId);
            newTemplate.withChangeNotes(changeNotes);
        }

        if (!isWorknestUser) {
            newTemplate.withSiteExternalId(
                selectedSite?.externalId ?? selectedAccount?.account?.externalId ?? ""
            );
        }

        const onError = (verb) => (error) => {
            console.error(error);
            // Validation failures arrive as ProblemDetails, so say which field was rejected
            // rather than "there was an error" and leaving people to guess.
            const validation = error?.errors && Object.values(error.errors).flat().join(" ");
            enqueueSnackbar(
                validation || error?.title || error?.message || `There was an error ${verb} the template`,
                { variant: "error" }
            );
        };

        if (!templateVersionId || (!isWorknestUser && isGlobalTemplate) || isCopy) {
            addNewTemplate.mutate(newTemplate.build(), { onSuccess: goBack, onError: onError("adding") });
        } else {
            updateTemplate.mutate(newTemplate.build(), { onSuccess: goBack, onError: onError("updating") });
        }
    }

    function onSubmit(e) {
        e.preventDefault();
        const error = validate();
        if (error) {
            enqueueSnackbar(error, { variant: "warning" });
            return;
        }
        // Editing an existing version requires change notes before saving.
        if (templateVersionId && !changeNotes) {
            setShowChangeNotesDialog(true);
            return;
        }
        save();
    }

    const isSaving = addNewTemplate.isLoading || updateTemplate.isLoading;
    const saveLabel = templateVersionId && isWorknestUser ? "Update template" : "Save template";

    return (
        <PageBase>
            <TemplateVersionHistoryDialog
                onClose={() => setVersionDialogOpen(false)}
                open={versionDialogOpen}
                templateId={selectedTemplateId}
            />
            <HazardPickerDialog
                open={hazardPickerOpen}
                onClose={() => setHazardPickerOpen(false)}
                hazards={hazards.data ?? []}
                selectedHazardIds={templateHazardIds}
                customHazards={newCustomHazards}
                siteExternalId={selectedAccount?.account?.externalId ?? ""}
                onSave={({ hazardIds, customHazards }) => {
                    setTemplateHazardIds(hazardIds);
                    setNewCustomHazards(customHazards);
                }}
            />

            <div className={classes.header}>
                <div>
                    <Typography variant="h5" className={classes.title}>
                        {templateVersionId && !isCopy ? "Edit Template" : "Create Template"}
                    </Typography>
                    <Typography variant="body2" className={classes.subtitle}>
                        Risk Assessment template{templateName ? ` — ${templateName}` : ""}
                    </Typography>
                </div>
                {templateVersionId && (
                    <Button variant="text" color="secondary" onClick={() => setVersionDialogOpen(true)}>
                        Version History
                    </Button>
                )}
            </div>

            <div className={classes.summaryChips}>
                <Chip
                    size="small"
                    color={detailsComplete ? "secondary" : "default"}
                    variant={detailsComplete ? "filled" : "outlined"}
                    label={detailsComplete ? "Details complete" : "Details"}
                />
                <Chip
                    size="small"
                    color={totalHazards > 0 ? "secondary" : "default"}
                    variant={totalHazards > 0 ? "filled" : "outlined"}
                    label={`${totalHazards} hazard${totalHazards === 1 ? "" : "s"}`}
                />
                <Chip
                    size="small"
                    color={totalPeople > 0 ? "secondary" : "default"}
                    variant={totalPeople > 0 ? "filled" : "outlined"}
                    label={`${totalPeople} ${totalPeople === 1 ? "person type" : "people types"}`}
                />
            </div>

            <form onSubmit={onSubmit}>
                <div className={classes.section}>
                    <CardBase flat>
                        <div className={classes.guidance}>
                            <Typography variant="subtitle2" gutterBottom>
                                Building a Risk Assessment template
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                Give the template a clear name and sector, choose the hazards it should
                                pre-populate, and select the people typically at risk. When someone
                                creates a risk assessment from this template, these hazards and people
                                are filled in for them to evaluate.
                            </Typography>
                        </div>
                    </CardBase>
                </div>

                <div className={classes.section}>
                    <CardBase title="Details">
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                    required
                                    fullWidth
                                    size="small"
                                    variant="outlined"
                                    label="Template Name"
                                    value={templateName ?? ""}
                                    onChange={(e) =>
                                        dispatch(setTemplateName({ text: e.target.value, hasUpdated: true }))
                                    }
                                />
                            </Grid>
                            {!isWorknestUser && (
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <SiteSelect size="small" />
                                </Grid>
                            )}
                            <Grid size={{ xs: 12, md: 6 }}>
                                <FormControl
                                    required
                                    fullWidth
                                    size="small"
                                    variant="outlined"
                                    disabled={sectors.isLoading}
                                    error={sectors.isError}
                                >
                                    <InputLabel id="ra-sector-label">Sector</InputLabel>
                                    <Select
                                        labelId="ra-sector-label"
                                        label="Sector"
                                        value={templateSectorId ?? ""}
                                        onChange={(e) =>
                                            dispatch(setTemplateSectorId({ sectorId: e.target.value, hasUpdated: true }))
                                        }
                                    >
                                        {sectors.data?.map((sector) => (
                                            <MenuItem key={sector.id} value={sector.id}>
                                                {sector.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {sectors.isError && (
                                        <FormHelperText>
                                            {sectors.error?.message || "Could not load sectors"}
                                        </FormHelperText>
                                    )}
                                </FormControl>
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <TextField
                                    required
                                    fullWidth
                                    multiline
                                    minRows={3}
                                    size="small"
                                    variant="outlined"
                                    label="Description"
                                    value={templateDescription}
                                    onChange={(e) => setTemplateDescription(e.target.value)}
                                />
                            </Grid>
                        </Grid>
                    </CardBase>
                </div>

                <div className={classes.section}>
                    <PeopleAtRiskCard
                        people={people.data ?? []}
                        selectedPeopleIds={templatePeopleIds}
                        customPeople={newCustomPeople}
                        siteExternalId={selectedAccount?.account?.externalId ?? ""}
                        onSave={({ peopleIds, customPeople }) => {
                            setTemplatePeopleIds(peopleIds);
                            setNewCustomPeople(customPeople);
                        }}
                    />
                </div>

                <div className={classes.section}>
                    <CardBase title="Hazards" description="Add the hazards this template should include.">
                        <Typography variant="body2" className={classes.selectedCount}>
                            {totalHazards === 0
                                ? "No hazards added yet."
                                : `${totalHazards} hazard${totalHazards === 1 ? "" : "s"} added`}
                        </Typography>

                        {selectedHazards.length > 0 && (
                            <Grid container spacing={1.5} className={classes.section}>
                                {selectedHazards.map((hazard) => (
                                    <Grid size={{ xs: 12, sm: 6, md: 4 }} key={hazard.id}>
                                        <div className={clsx(classes.tile, classes.tileSelected, classes.tileStatic)}>
                                            <img
                                                className={classes.tileIcon}
                                                alt=""
                                                src={`${hazardImagesUrl}${hazard.name}.svg`}
                                                onError={(e) => {
                                                    (e.currentTarget as HTMLImageElement).src = fallbackHazardIcon;
                                                }}
                                            />
                                            <Typography className={classes.tileLabel}>{hazard.name}</Typography>
                                            <IconButton
                                                size="small"
                                                aria-label={`Remove ${hazard.name}`}
                                                onClick={() => removeHazard(hazard.id)}
                                            >
                                                <Close fontSize="small" />
                                            </IconButton>
                                        </div>
                                    </Grid>
                                ))}
                            </Grid>
                        )}

                        {newCustomHazards.length > 0 && (
                            <div className={classes.customList}>
                                {newCustomHazards.map((hazard) => {
                                    const controls = hazard.controls ?? [];
                                    const draft = controlDrafts[hazard.id] ?? "";
                                    return (
                                        <div className={classes.customHazardRow} key={hazard.id}>
                                            <div className={classes.customHazardHead}>
                                                <WarningAmber color="secondary" />
                                                <Typography className={classes.customHazardName}>{hazard.name}</Typography>
                                                <Chip size="small" label="Custom" color="secondary" variant="outlined" />
                                                <span className={classes.headSpacer} />
                                                <IconButton
                                                    size="small"
                                                    aria-label={`Remove ${hazard.name}`}
                                                    onClick={() => removeCustomHazard(hazard.id)}
                                                >
                                                    <Close fontSize="small" />
                                                </IconButton>
                                            </div>
                                            <div className={classes.customControls}>
                                                <Typography className={classes.controlsLabel}>Control measures</Typography>
                                                {controls.length === 0 ? (
                                                    <Typography className={classes.noControls}>
                                                        Adding controls here is optional — you can add them now, or when
                                                        completing the actual assessment.
                                                    </Typography>
                                                ) : (
                                                    controls.map((ctrl) => (
                                                        <div className={classes.customControlRow} key={ctrl.id}>
                                                            <Typography className={classes.customControlName}>• {ctrl.name}</Typography>
                                                            <IconButton
                                                                size="small"
                                                                aria-label={`Remove control ${ctrl.name}`}
                                                                onClick={() => removeCustomHazardControl(hazard.id, ctrl.id)}
                                                            >
                                                                <Close fontSize="small" />
                                                            </IconButton>
                                                        </div>
                                                    ))
                                                )}
                                                <div className={classes.addControlRow}>
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        variant="outlined"
                                                        placeholder="Add a control measure"
                                                        value={draft}
                                                        onChange={(e) =>
                                                            setControlDrafts((d) => ({ ...d, [hazard.id]: e.target.value }))
                                                        }
                                                        onKeyDown={(e) => {
                                                            if (e.key === "Enter") {
                                                                e.preventDefault();
                                                                addCustomHazardControl(hazard.id);
                                                            }
                                                        }}
                                                    />
                                                    <Button
                                                        variant="outlined"
                                                        color="secondary"
                                                        startIcon={<Add />}
                                                        disabled={!draft.trim()}
                                                        onClick={() => addCustomHazardControl(hazard.id)}
                                                    >
                                                        Add
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        <Button
                            fullWidth
                            variant="outlined"
                            color="secondary"
                            startIcon={<Add />}
                            onClick={() => setHazardPickerOpen(true)}
                            sx={{ borderStyle: "dashed", py: 1.5 }}
                        >
                            Add hazard
                        </Button>
                    </CardBase>
                </div>

                <div className={classes.footer}>
                    <Button variant="outlined" color="primary" type="button" onClick={goBack}>
                        Cancel
                    </Button>
                    <div className={classes.footerButtons}>
                        <CustomButton variant="contained" color="primary" type="submit" isLoading={isSaving}>
                            {saveLabel}
                        </CustomButton>
                    </div>
                </div>
            </form>

            <Dialog open={showChangeNotesDialog} maxWidth="md" fullWidth>
                <DialogTitle>Add version change notes</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Add change notes to the updated template version for users to view changes.
                    </DialogContentText>
                    <TextField
                        size="small"
                        fullWidth
                        multiline
                        minRows={2}
                        label="Change Notes"
                        variant="outlined"
                        value={changeNotes}
                        onChange={(e) => setChangeNotes(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setShowChangeNotesDialog(false);
                            setChangeNotes("");
                        }}
                        color="primary"
                        variant="outlined"
                    >
                        Cancel
                    </Button>
                    <Button
                        color="primary"
                        variant="contained"
                        disabled={!changeNotes || isSaving}
                        onClick={() => {
                            setShowChangeNotesDialog(false);
                            save();
                        }}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        </PageBase>
    );
}
